// client/src/components/pages/LexiStar/StatusBoard/fuelLogic.js
//
// Display logic for the fuel and distance figures on the control-tower pages.
// The engine computes the numbers (LS-04 §9, M2-D2); this module only decides
// how they read: burn-index bands (FR-STAT-005), the over-consumption tag
// (FR-FUEL-005 / D22 — informs, never blocks), provenance markers (FR-STAT-008)
// and the distance sense check (FR-STAT-006).
//
// overConsumption() re-states the LS-04 §9 close-of-leg rule so a row the
// server has not flagged yet (a live leg that has already burned through its
// allowance) still shows the tag. A server-supplied `over_consumption` always
// wins.

// ── Burn index (FR-STAT-005) ────────────────────────────────────────────────
export const BURN_INDEX_NORMAL_MAX = 1.05;
export const BURN_INDEX_WARNING_MAX = 1.15;
/** Suppressed until this share of the route is complete. */
export const BURN_INDEX_MIN_PCT_DISTANCE = 0.1;

/**
 * @param {number|null} burnIndex  % budget consumed ÷ % distance completed
 * @param {number|null} pctDistance 0–1; when known and < 10 % the index is suppressed
 * @returns {'suppressed'|'normal'|'warning'|'critical'}
 */
export function burnIndexBand(burnIndex, pctDistance) {
  if (burnIndex === null || burnIndex === undefined || Number.isNaN(Number(burnIndex))) {
    return 'suppressed';
  }
  if (
    pctDistance !== null &&
    pctDistance !== undefined &&
    Number(pctDistance) < BURN_INDEX_MIN_PCT_DISTANCE
  ) {
    return 'suppressed';
  }
  const bi = Number(burnIndex);
  if (bi <= BURN_INDEX_NORMAL_MAX) return 'normal';
  if (bi <= BURN_INDEX_WARNING_MAX) return 'warning';
  return 'critical';
}

export const BURN_BAND_META = {
  suppressed: {
    label: 'Not yet meaningful (under 10% of the route)',
    className: 'text-slate-400',
    arrow: '',
  },
  normal: { label: 'Within norm (≤ 1.05)', className: 'text-slate-700', arrow: '' },
  warning: {
    label: 'Above norm (1.05–1.15)',
    className: 'text-amber-700 font-semibold',
    arrow: '▲',
  },
  critical: {
    label: 'Well above norm (> 1.15)',
    className: 'text-red-700 font-semibold',
    arrow: '▲',
  },
};

// ── Provenance (FR-STAT-008, LS-09 §3) ──────────────────────────────────────
export const PROVENANCE_META = {
  measured: {
    glyph: '●',
    label: 'Measured',
    help: 'Measured — from the vehicle (CAN total fuel, fuel probe or odometer).',
  },
  derived: {
    glyph: '◐',
    label: 'Derived',
    help: 'Derived — calculated from GPS positions or fuel-level changes.',
  },
  estimated: {
    glyph: '○',
    label: 'Estimated',
    help: 'Estimated — from the fuel norm for this vehicle class and route.',
  },
};

/** 'Measured' | 'CAN' | 'probe' … → 'measured' | 'derived' | 'estimated' | null */
export function normaliseProvenance(p) {
  if (!p) return null;
  const v = String(p).toLowerCase();
  if (v in PROVENANCE_META) return v;
  if (['can', 'probe', 'odometer', 'can_total_fuel'].includes(v)) return 'measured';
  if (['gps', 'level_delta', 'level'].includes(v)) return 'derived';
  if (['norm', 'estimate'].includes(v)) return 'estimated';
  return null;
}

// ── Over-consumption (LS-04 §9, FR-FUEL-005, FR-FUEL-013) ───────────────────
export const OVER_TOLERANCE_PCT = 0.03;
export const OVER_TOLERANCE_MIN_L = 2;
export const OVER_HIGH_PCT = 0.1;

/**
 * The close-of-leg rule: flagged when consumed exceeds the adjusted allowance
 * by more than max(3 %, 2 L); medium up to 10 % over, high beyond; confirmed
 * only when consumption is Measured, indicative otherwise.
 * @returns {{excess_l:number, excess_pct:number, severity:'medium'|'high',
 *            certainty:'confirmed'|'indicative'}|null}
 */
export function overConsumption({ consumed_l, budget_l, provenance }) {
  const consumed = Number(consumed_l);
  const budget = Number(budget_l);
  if (!Number.isFinite(consumed) || !Number.isFinite(budget) || budget <= 0) return null;
  const excess = consumed - budget;
  const tolerance = Math.max(OVER_TOLERANCE_PCT * budget, OVER_TOLERANCE_MIN_L);
  if (excess <= tolerance) return null;
  const excessPct = excess / budget;
  return {
    excess_l: Math.round(excess * 10) / 10,
    excess_pct: excessPct,
    severity: excessPct <= OVER_HIGH_PCT ? 'medium' : 'high',
    certainty: normaliseProvenance(provenance) === 'measured' ? 'confirmed' : 'indicative',
  };
}

/**
 * What the fuel cell's litre tag shows: `+6.5 L`, amber (medium) or red (high),
 * hollow when indicative. Uses the server's flag when present, else the rule
 * above against the adjusted (else original) allowance.
 * @param {Object|null} fuel status-board / trip fuel block
 * @returns {{text:string, excess_l:number, severity:string, certainty:string,
 *            tone:'amber'|'red', hollow:boolean, className:string, title:string}|null}
 */
export function overConsumptionTag(fuel) {
  if (!fuel) return null;
  const flag =
    fuel.over_consumption ||
    overConsumption({
      consumed_l: fuel.consumed_l,
      budget_l: fuel.budget_adjusted_l ?? fuel.budget_original_l ?? fuel.budget_l,
      provenance: fuel.provenance,
    });
  if (!flag || !(Number(flag.excess_l) > 0)) return null;
  const tone = flag.severity === 'high' ? 'red' : 'amber';
  const hollow = flag.certainty !== 'confirmed';
  const solid = tone === 'red' ? 'bg-red-600 text-red-50' : 'bg-amber-500 text-amber-950';
  const outline =
    tone === 'red'
      ? 'bg-transparent text-red-700 ring-1 ring-inset ring-red-500'
      : 'bg-transparent text-amber-700 ring-1 ring-inset ring-amber-500';
  const litres = Number(flag.excess_l);
  return {
    text: `+${litres.toFixed(1)} L`,
    excess_l: litres,
    severity: flag.severity,
    certainty: flag.certainty || 'indicative',
    tone,
    hollow,
    className: hollow ? outline : solid,
    title:
      `Over allowance by ${litres.toFixed(1)} L (${flag.severity === 'high' ? 'more than 10%' : 'up to 10%'})` +
      (hollow
        ? ' — indicative: consumption is not measured'
        : ' — confirmed: measured consumption') +
      '. The allowance flags over-consumption; it never blocks.',
  };
}

// ── Distance sense check (FR-STAT-006, LS-04 §4.2) ──────────────────────────
export const ROUTE_NORM_TOL_PCT = 0.05;
export const ROUTE_NORM_MIN_KM = 3;

/**
 * Server flag wins (`above_route_norm`, or the engine's `distance_above_route_norm`
 * trip flag); otherwise km > avg × 1.05 and at least 3 km above.
 */
export function isAboveRouteNorm(trip) {
  if (!trip) return false;
  if (typeof trip.above_route_norm === 'boolean') return trip.above_route_norm;
  if ((trip.status_flags || trip.flags || []).includes('distance_above_route_norm')) return true;
  const km = Number(trip.actual_km ?? trip.km_travelled);
  const avg = Number(trip.route_avg_km);
  if (!Number.isFinite(km) || !Number.isFinite(avg) || avg <= 0) return false;
  if (!['COMPLETED', 'RECONCILED', 'CLOSED'].includes(trip.stage)) return false;
  return km > avg * (1 + ROUTE_NORM_TOL_PCT) && km - avg >= ROUTE_NORM_MIN_KM;
}
